import type { CompareData } from '../Compare.types';

type Translate = (key: string) => string;

export const getSayariCompareData = (t: Translate): CompareData => ({
  competitor: 'Sayari',
  competitorHeader: 'Sayari',
  competitorLogo: { type: 'logo-sayari', label: 'Sayari' },
  heroTitle: 'Global Database vs Sayari',
  heroDescription: (
    <>
      {t('Sayari is fundamentally a')} <b>{t('data aggregator')}</b>
      {t(', built for supply-chain and ownership-risk intelligence on top of licensed third-party data — with')}{' '}
      <b>{t('strict reseller rights')}</b>{' '}
      {t(
        'that limit how far its data can travel downstream. Global Database sources every record directly, digitizes financials with OCR and AI, and adds transfer pricing, M&A history, and an ownership graph connecting 45M+ companies and 500M+ people.',
      )}
    </>
  ),
  quickItems: [
    <>
      <b>{t('Direct-sourced data')}</b> {t('— not blended from licensed third-party feeds')}
    </>,
    <>
      <b>{t('Flexible reseller rights')}</b> {t('— no strict resale lock-in on enterprise plans')}
    </>,
    <>
      <b>{t('Transfer pricing & M&A history')}</b> {t('included as standard')}
    </>,
    <>
      <b>{t('45M+ companies, 500M+ people')}</b> {t('mapped in one global ownership graph')}
    </>,
  ],

  table: {
    title: t('Global Database vs Sayari at a glance'),
    description: t(
      'Sayari is built for supply-chain mapping and ownership-risk investigation, drawing on a network of licensed and public data sources with tightly controlled resale terms. Global Database sources directly and shows its work.',
    ),
    rows: [
      {
        attribute: t('Data sourcing model'),
        gd: t('Direct sourcing, with source, link, timestamp and official filing published per record'),
        them: t('Data aggregator — combines licensed and public data sources for supply-chain and risk mapping'),
      },
      {
        attribute: t('Reseller rights'),
        gd: t('Flexible — enterprise clients can integrate, resell, or build derivative products'),
        them: t("Strict — resale and redistribution are tightly controlled under Sayari's licensing terms"),
      },
      {
        attribute: t('Financials'),
        gd: t('Digitized via OCR + AI from filed statements, mapped consistently across countries'),
        them: t('Focus is on entity and network mapping rather than digitized financial statements'),
      },
      {
        attribute: t('Ownership graph'),
        gd: t('45M+ companies and 500M+ people connected in one global graph'),
        them: t('Network/graph analysis strong for risk investigation, built on licensed and public sources'),
      },
      {
        attribute: t('Transfer pricing'),
        gd: t('Included'),
        them: t('Not a core part of the standard offering'),
      },
      {
        attribute: t('M&A & deal history'),
        gd: t('Included'),
        them: t('Not a core part of the standard offering'),
      },
      {
        attribute: t('Bank account verification'),
        gd: t('Included'),
        them: t('Not a core part of the standard offering'),
      },
    ],
  },

  stats: {
    title: t('Why sourcing model matters'),
    description: t(
      'An aggregator is only as current and traceable as its weakest upstream vendor. Direct sourcing means every fact can be traced back to where it came from.',
    ),
    items: [
      { value: '45M+', label: t('Companies in the ownership graph') },
      { value: '500M+', label: t('People connected to companies') },
      { value: '20 yrs', label: t('Digitized financial history') },
      { value: '3–5/mo', label: t('New jurisdictions added') },
    ],
    note: t(
      "Every Global Database record carries its source, a link to it, a timestamp, and — where applicable — the official filing it was drawn from. That's a structurally different guarantee than a licensed network-analysis feed: it means a risk or investigative decision can point back to a primary document, not a vendor's aggregation of one.",
    ),
  },

  difference: {
    title: t("What's the difference between Global Database and Sayari?"),
    paragraphs: [
      <>
        <b>Sayari</b>{' '}
        {t(
          "is, at its core, a data aggregator purpose-built for supply-chain mapping and ownership-risk investigation — combining licensed commercial data with public sources into a network-analysis platform. That's a genuinely useful lens for tracing complex corporate networks, but it comes with the same trade-off most aggregators carry:",
        )}{' '}
        <b>{t('reseller rights are strict')}</b>
        {t(", and financial-statement depth is not the platform's focus.")}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          'takes a different starting point: data is sourced directly, with the source, link, timestamp, and official filing published against each record. On top of that direct-sourced layer, Global Database adds OCR-and-AI-digitized financials mapped consistently across countries, transfer pricing data, M&A and deal history, bank account verification, and a global ownership graph spanning 45M+ companies and 500M+ people — with flexible reseller rights for enterprise clients who want to build on top of it.',
        )}
      </>,
      <>
        {t(
          "For deep supply-chain network investigation specifically, Sayari's tooling is well regarded. Where flexible resale rights, digitized financial depth, or transfer pricing and deal history in one platform is the requirement, that's where Global Database is built to lead.",
        )}
      </>,
    ],
    pull: t(
      '"Network analysis is only as strong as the underlying entity data feeding it. Directly sourced, digitized financials give that network a financial dimension most aggregators can\'t."',
    ),
  },

  features: {
    title: t('Key features compared'),
    description: t("A closer look at what's included on each platform."),
    gd: [
      {
        title: t('Direct sourcing, fully traceable'),
        text: t('Source, link, timestamp and official filing published for every record.'),
      },
      {
        title: t('Flexible reseller rights'),
        text: t('Enterprise clients can integrate, resell, or build derivative products on the data.'),
      },
      {
        title: t('OCR + AI digitized financials'),
        text: t('Financial statements extracted and mapped consistently across countries.'),
      },
      {
        title: t('45M+ company / 500M+ people ownership graph'),
        text: t('A single graph connecting individuals to companies at global scale.'),
      },
      {
        title: t('Transfer pricing'),
        badge: t('Included'),
        text: t('Transfer pricing data included as part of the standard record, not a separate purchase.'),
      },
      {
        title: t('M&A & deal history'),
        text: t('Transaction history included as part of the core company record.'),
      },
      {
        title: t('Bank account verification'),
        text: t('Verification included as part of the standard data layer.'),
      },
    ],
    them: [
      {
        title: t('Supply-chain network mapping'),
        text: t('Purpose-built tooling for tracing corporate and trade networks across licensed and public sources.'),
      },
      {
        title: t('Strict reseller rights'),
        text: t('Redistribution and resale of Sayari data are tightly controlled under its licensing terms.'),
      },
      {
        title: t('Risk investigation focus'),
        text: t('Strong for ownership-risk and sanctions-exposure investigation workflows.'),
      },
      {
        title: t('Ownership data via licensed sources'),
        text: t('Network graph built from a combination of commercial and public data feeds.'),
      },
      {
        title: t('Limited financial depth'),
        text: t('Not built around digitized financial statements the way a financial-data platform is.'),
      },
      {
        title: t('Established in trade & compliance'),
        text: t('Recognised tooling in supply-chain due diligence and trade-compliance teams.'),
      },
    ],
  },

  extra: {
    id: 'reseller',
    title: t('Reseller rights compared'),
    description: (
      <em>
        {t(
          'Licensing terms vary by contract and use case — confirm specifics directly with either provider. The structural difference is below.',
        )}
      </em>
    ),
    paragraphs: [
      <>
        <b>Sayari</b>,{' '}
        {t(
          'as an aggregator built for network and supply-chain analysis, licenses much of its underlying data from commercial and public sources — which means resale and redistribution of Sayari data are governed by strict, tightly controlled licensing terms rather than a simple usage policy.',
        )}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          "offers flexible reseller rights on enterprise plans: clients can integrate the data into their own product, resell it, or build derivative products directly on top of it, since the data is sourced and owned by Global Database's own pipeline rather than re-licensed from elsewhere.",
        )}
      </>,
    ],
  },

  faq: {
    title: t('FAQs about Global Database vs Sayari'),
    data: [
      {
        id: 'is-sayari-a-data-aggregator',
        default_opened: true,
        question: t('Is Sayari a data aggregator?'),
        answer: t(
          'Sayari combines licensed commercial data with public sources to power its supply-chain and ownership-risk network analysis. This gives it strong tooling for tracing corporate networks, but the freshness and provenance of any given record depend on the underlying source it was drawn from.',
        ),
      },
      {
        id: 'can-i-resell-sayari-data',
        question: t('Can I resell Sayari data in my own product?'),
        answer: t(
          "Resale terms depend on Sayari's licensing agreements with its underlying data sources, and typically require specific contractual approval. Global Database offers flexible reseller rights as standard on enterprise plans, since its data is sourced directly rather than re-licensed.",
        ),
      },
      {
        id: 'does-gdb-offer-same-network-mapping',
        question: t('Does Global Database offer the same network mapping as Sayari?'),
        answer: t(
          "Global Database's ownership graph connects more than 45 million companies and 500 million people, mapping ownership and control across jurisdictions. Sayari's tooling is purpose-built for supply-chain and trade-network investigation specifically. The right fit depends on whether the priority is directly-sourced, financially-rich company data or dedicated network-investigation tooling.",
        ),
      },
      {
        id: 'does-gdb-include-transfer-pricing-ma',
        question: t('Does Global Database include transfer pricing and M&A history?'),
        answer: t(
          "Yes — both are included as part of Global Database's standard company record, alongside digitized financials, bank account verification, and the ownership graph.",
        ),
      },
      {
        id: 'does-gdb-digitize-financials',
        question: t("Does Global Database digitize financial statements the way Sayari doesn't?"),
        answer: t(
          "Yes. Global Database uses OCR and AI to digitize financial statements from filed documents and maps them consistently across countries, with up to 20 years of history. Financial-statement depth is not the core focus of Sayari's network-analysis platform.",
        ),
      },
      {
        id: 'is-gdb-a-good-sayari-alternative',
        question: t('Is Global Database a good Sayari alternative for compliance and risk teams?'),
        answer: t(
          'For teams that need directly-sourced, traceable data — plus digitized financials, transfer pricing, and flexible reseller rights — Global Database is commonly evaluated as a strong alternative to a strictly licensed network-analysis aggregator like Sayari.',
        ),
      },
    ],
  },

  cta: {
    title: t('See Global Database on your own company records'),
    description: t(
      "Request a data review and compare sourcing, ownership resolution, and reseller terms directly against what you're using today.",
    ),
  },
});
